import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Checkbox component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 177:1276):
 * - Size: 16x16px box in 24x24px container
 * - Border Radius: 4px
 * - Unchecked: White bg, subtle shadow border
 * - Checked: Grey-950 bg (#09090B), white checkmark
 * - Focus: 4px ring at 15% opacity
 */
const checkboxVariants = cva(
  'peer h-4 w-4 shrink-0 rounded border transition-all duration-100 focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'border-grey-200 bg-white shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.1)] data-[state=checked]:bg-grey-950 data-[state=checked]:border-grey-950 data-[state=checked]:shadow-[0px_0px_0px_1px_#121212] data-[state=indeterminate]:bg-grey-950 data-[state=indeterminate]:border-grey-950 hover:border-grey-300 hover:shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.15),0px_1px_3px_0px_rgba(18,18,18,0.1)] focus-visible:ring-grey-950/15 dark:border-grey-700 dark:bg-grey-900 dark:data-[state=checked]:bg-grey-50 dark:data-[state=checked]:border-grey-50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Checkbox = React.forwardRef(({ className, variant, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(checkboxVariants({ variant }), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-white dark:text-grey-950">
      {props.checked === 'indeterminate' ? (
        <Minus className="h-3 w-3" strokeWidth={3} />
      ) : (
        <Check className="h-3 w-3" strokeWidth={3} />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

/**
 * CheckboxField - Checkbox with label and optional description
 *
 * Usage:
 *   <CheckboxField label="Remember me" />
 *   <CheckboxField label="Accept terms" description="You agree to our terms of service" />
 */
const CheckboxField = React.forwardRef(
  ({ className, label, description, id, ...props }, ref) => {
    const autoId = React.useId();
    const generatedId = id || autoId;
    return (
      <div className={cn('flex items-start gap-3', className)}>
        <Checkbox ref={ref} id={generatedId} {...props} />
        <div className="flex flex-col gap-0.5">
          <label
            htmlFor={generatedId}
            className="text-sm font-medium leading-5 text-grey-950 cursor-pointer dark:text-grey-50"
          >
            {label}
          </label>
          {description && (
            <p className="text-xs text-grey-500 leading-4">{description}</p>
          )}
        </div>
      </div>
    );
  }
);
CheckboxField.displayName = 'CheckboxField';

export { Checkbox, CheckboxField, checkboxVariants };
